import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import {
  AtividadeEconomicaDTO,
  AtividadeEconomicaFormDTO,
} from '../../../models/configuracoes/atividade-economica.model';
import { AtividadeEconomicaService } from '../../../services/configuracoes/atividade-economica.service';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../../models/shared/table-column.model';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-atividade-economica',
  templateUrl: './configuracao-atividade-economica.component.html',
  styleUrls: ['./configuracao-atividade-economica.component.scss'],
  providers: [MessageService],
})
export class AtividadeEconomicaComponent implements OnInit, OnDestroy {
  // Controle de abas
  activeTabIndex: number = 1;

  // Controle de validacao visual
  isFormSubmitted = false;
  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  placeholders: any = {};
  botoes: any = {};
  tooltips: any = {};

  private langChangeSub!: Subscription;

  atividades: AtividadeEconomicaDTO[] = [];
  atividadesOriginal: AtividadeEconomicaDTO[] = [];
  form: AtividadeEconomicaFormDTO = this.createEmptyForm();
  searchNome = '';
  loading = true;
  first: number = 0;
  rows = 10;
  totalRecords = 0;
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'configuracao-atividade-economica-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'configuracao-atividade-economica.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'configuracao-atividade-economica.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigo', labelKey: 'configuracao-atividade-economica.labels.codigo', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'configuracao-atividade-economica.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'configuracao-atividade-economica.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  private search$ = new Subject<string>();

  constructor(
    private messageService: MessageService,
    private atividadeEconomicaService: AtividadeEconomicaService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.findAtividade();
    this.setupSearchDebounce();
    this.loadTranslations();
    this.loadColumnPreferences();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('configuracao-atividade-economica.texts.titulo');
      this.texts.subtitulo = this.translate.instant('configuracao-atividade-economica.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'configuracao-atividade-economica.texts.titulo_editar' : 'configuracao-atividade-economica.texts.titulo'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'configuracao-atividade-economica.texts.subtitulo_editar' : 'configuracao-atividade-economica.texts.subtitulo_nova'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'configuracao-atividade-economica.texts.titulo' : isEdicao ? 'configuracao-atividade-economica.texts.titulo_editar' : 'configuracao-atividade-economica.texts.titulo'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'configuracao-atividade-economica.texts.subtitulo_lista'
          : isEdicao ? 'configuracao-atividade-economica.texts.subtitulo_editar' : 'configuracao-atividade-economica.texts.subtitulo_nova'
      ),
      dados_da_atividade: this.translate.instant('configuracao-atividade-economica.texts.dados_da_atividade'),
      tabela_de_atividades: this.translate.instant('configuracao-atividade-economica.texts.tabela_de_atividades'),
      nenhuma_atividade: this.translate.instant('configuracao-atividade-economica.texts.nenhuma_atividade'),
      ajuste_filtros: this.translate.instant('configuracao-atividade-economica.texts.ajuste_filtros'),
      carregando: this.translate.instant('configuracao-atividade-economica.texts.carregando'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_exclusao: this.translate.instant('configuracao-atividade-economica.texts.confirmar_exclusao'),
      aba_cadastro: this.translate.instant('configuracao-atividade-economica.texts.aba_cadastro'),
      aba_lista: this.translate.instant('configuracao-atividade-economica.texts.aba_lista'),
    };

    this.labels = {
      id: this.translate.instant('configuracao-atividade-economica.labels.id'),
      nome: this.translate.instant('configuracao-atividade-economica.labels.nome'),
      codigo: this.translate.instant('configuracao-atividade-economica.labels.codigo'),
      data_inclusao: this.translate.instant('configuracao-atividade-economica.labels.data_inclusao'),
      acoes: this.translate.instant('configuracao-atividade-economica.labels.acoes'),
      itens_por_pagina: this.translate.instant('configuracao-atividade-economica.labels.itens_por_pagina'),
    };

    this.placeholders = {
      nome: this.translate.instant('configuracao-atividade-economica.placeholders.nome'),
      codigo: this.translate.instant('configuracao-atividade-economica.placeholders.codigo'),
      pesquisar: this.translate.instant('configuracao-atividade-economica.placeholders.pesquisar'),
    };

    this.botoes = {
      salvar: this.translate.instant('configuracao-atividade-economica.botoes.salvar'),
      atualizar: this.translate.instant('configuracao-atividade-economica.botoes.atualizar'),
      limpar: this.translate.instant('configuracao-atividade-economica.botoes.limpar'),
      nova_atividade: this.translate.instant('configuracao-atividade-economica.botoes.nova_atividade'),
      cancelar: this.translate.instant('common.cancelar'),
      confirmar: this.translate.instant('common.confirmar'),
    };

    this.tooltips = {
      editar: this.translate.instant('configuracao-atividade-economica.tooltips.editar'),
      excluir: this.translate.instant('configuracao-atividade-economica.tooltips.excluir'),
    };
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  findAtividade(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ) {
    this.loading = true;
    this.atividadeEconomicaService
      .get('/atividadeEconomica', { page, size, sort })
      .subscribe({
        next: (response) => {
          this.atividadesOriginal = response.content || [];
          this.atividades = [...this.atividadesOriginal];
          this.totalRecords = response.totalElements || this.atividadesOriginal.length;
          this.first = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarAtividadesLocal(this.searchNome.trim());
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('Erro_ao_carregar_atividades_economicas'),
          });
          this.loading = false;
        },
      });
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.rows = event.rows;
    this.findAtividade(page, size);
  }

  onRowsPerPageChange() {
    this.first = 0;
    this.findAtividade(0, this.rows);
  }

  customSort(event: SortEvent): void {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.first = 0;
      if (term && term.trim().length > 0) {
        this.filtrarAtividadesLocal(term.trim());
      } else {
        this.atividades = [...this.atividadesOriginal];
        this.totalRecords = this.atividadesOriginal.length;
      }
    });
  }

  onSearchChange(value: string) {
    this.searchNome = value || '';
    this.search$.next(this.searchNome);
  }

  /** Filtra atividades economicas localmente buscando em todas as colunas visiveis */
  private filtrarAtividadesLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.atividades = this.atividadesOriginal.filter(atividade => {
      if (this.isColumnVisible('id') && atividade.id != null) {
        if (String(atividade.id).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && atividade.nome) {
        if (atividade.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('codigo') && atividade.codigo) {
        if (atividade.codigo.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && (atividade as any).criadoEm) {
        const dataFormatada = new Date((atividade as any).criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.atividades.length;
  }

  submit() {
    this.isFormSubmitted = true;

    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_nome_atividade_obrigatorio'),
      });
      return;
    }

    if (!this.form.codigo) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_codigo_atividade_obrigatorio'),
      });
      return;
    }

    const body: AtividadeEconomicaDTO = {
      nome: this.form.nome,
      codigo: this.form.codigo,
    };

    this.atividadeEconomicaService.post('/atividadeEconomica', body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Atividade_economica_salva_com_sucesso'),
        });
        this.isFormSubmitted = false;
        this.form = this.createEmptyForm();
        this.loadTranslations();
        this.findAtividade();
        this.activeTabIndex = 1;
      },
      error: () => {},
    });
  }

  salvarEdicao() {
    this.isFormSubmitted = true;

    if (!this.form.nome || this.form.nome.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_nome_atividade_obrigatorio'),
      });
      return;
    }

    if (!this.form.codigo) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_codigo_atividade_obrigatorio'),
      });
      return;
    }

    if (!this.form.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Atividade_invalida_para_edicao'),
      });
      return;
    }

    const body: AtividadeEconomicaDTO = {
      nome: this.form.nome,
      codigo: this.form.codigo,
    };

    this.atividadeEconomicaService.put('/atividadeEconomica/' + this.form.id, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Atividade_economica_editada_com_sucesso'),
        });
        this.isFormSubmitted = false;
        this.limparForm();
        this.findAtividade();
        this.activeTabIndex = 1;
      },
      error: () => {},
    });
  }

  deletarAtividadeEconomica(id: number) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (this.deleteItemId == null) return;
    this.atividadeEconomicaService
      .delete(`/atividadeEconomica/${this.deleteItemId}`)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('Atividade_economica_deletada_com_sucesso!'),
          });
          this.findAtividade();
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
        error: () => {
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
      });
  }

  createEmptyForm(): AtividadeEconomicaFormDTO {
    return {
      nome: '',
      codigo: '',
    };
  }

  editarAtividadeEconomica(event: AtividadeEconomicaFormDTO) {
    this.form = { ...event };
    this.activeTabIndex = 0;
    this.texts.titulo = this.translate.instant('configuracao-atividade-economica.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('configuracao-atividade-economica.texts.subtitulo_editar');
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novaAtividade() {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  limparForm() {
    this.isFormSubmitted = false;
    this.form = this.createEmptyForm();
    this.loadTranslations();
  }
}
